// Deterministic turn animation: interpolates a layer turn between two model
// states. During a turn the pose is a rigid rotation of the affected slots;
// on commit the model state advances and the pose resets. This keeps frame
// advancement exact (no error accumulation).

import * as THREE from "three";

// The model's integer-coordinate quarter turns are not all the same sign as
// Three.js' right-handed axis rotations. Keep this boundary explicit so a
// rendered layer arrives at the exact pose the model commits.
export const AXIS_ROTATION_SIGN = Object.freeze([-1, 1, -1]);

export function targetAngleForMove(move, fromAngle = 0) {
  const quarter = (AXIS_ROTATION_SIGN[move.axis] * Math.PI) / 2;
  if (move.times === 3) return -quarter;
  if (move.times === 2) return Math.sign(fromAngle || quarter) * Math.PI;
  return quarter;
}

export class TurnAnimator {
  constructor(view, { onCommit, animate = true, reducedMotion = false } = {}) {
    this.view = view;
    this.onCommit = onCommit;
    this.animate = animate && !reducedMotion;
    this.active = null; // { move, from, to, start, duration, easing, current }
    this.busy = false;
  }

  get isBusy() {
    return this.active !== null;
  }

  // Start an animated (or instant) turn. `fromAngle` allows seamless
  // continuation of a drag. The model move commits when the pose completes.
  start(move, { fromAngle = 0, duration = 0 } = {}) {
    if (this.active) return false;
    // A three-quarter internal turn is shown as its equivalent single inverse
    // turn. That is one physical counter-turn, not three visible turns.
    const to = targetAngleForMove(move, fromAngle);
    const dur = duration;
    if (!this.animate || dur <= 0) {
      if (fromAngle) this.view.resetPose(move);
      this.onCommit(move);
      return true;
    }
    this.active = {
      move,
      from: fromAngle,
      to,
      start: performance.now(),
      duration: dur * (move.times === 2 ? 1.3 : 1),
      easing: easeInOutCubic,
    };
    this.busy = true;
    this.view.setTurnPose(move, fromAngle);
    return true;
  }

  // Advance the animation; returns true when a turn committed this frame.
  update(now) {
    if (!this.active) return false;
    const a = this.active;
    // Compare timestamps directly at the completion boundary. Subtracting two
    // large fractional performance timestamps can round an exact duration to
    // 99.999999..., leaving the pose finished while skipping its commit.
    const done = now >= a.start + a.duration;
    let t = done ? 1 : (now - a.start) / a.duration;
    t = Math.min(1, Math.max(0, t));
    const angle = a.from + (a.to - a.from) * a.easing(t);
    this.view.setTurnPose(a.move, angle);
    if (done) {
      const move = a.move;
      this.active = null;
      this.busy = false;
      this.view.resetPose(move);
      this.onCommit(move);
      return true;
    }
    return false;
  }

  cancel() {
    const move = this.active?.move;
    this.active = null;
    this.busy = false;
    this.view.resetPose(move);
  }
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
